import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { kuunda } from '@/lib/kuunda';
import {
  getOAuthRedirectUrl,
  isNativeApp,
  NATIVE_OAUTH_HOST,
  NATIVE_OAUTH_SCHEME,
} from '@/lib/nativeApp';

const REFERRAL_STORAGE_KEY = 'isbd_oauth_referral';

export function rememberOAuthReferral(token: string | undefined) {
  const value = token?.trim();
  if (value) sessionStorage.setItem(REFERRAL_STORAGE_KEY, value);
  else sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
}

export function consumeOAuthReferral(): string | null {
  const value = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
  sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  return value;
}

function hasAuthPayload(url: URL): boolean {
  return (
    url.searchParams.has('code') ||
    url.searchParams.has('token_hash') ||
    url.searchParams.has('token') ||
    url.searchParams.has('error') ||
    url.searchParams.has('error_description') ||
    url.hash.includes('access_token') ||
    url.hash.includes('error=')
  );
}

/**
 * Transforme le deep link natif (custom scheme ou https://localhost)
 * en chemin SPA /auth/callback pour hydrater la session (hash #access_token=).
 */
function callbackPathFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const scheme = parsed.protocol.replace(/:$/, '');
    const isNativeScheme =
      scheme === NATIVE_OAUTH_SCHEME &&
      (parsed.hostname === NATIVE_OAUTH_HOST || parsed.host.startsWith(NATIVE_OAUTH_HOST));
    const isLegacyLocalhost =
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      parsed.hostname === 'localhost' &&
      (parsed.pathname === '/auth/callback' || parsed.pathname.endsWith('/auth/callback'));
    const isHttpsCallback =
      parsed.pathname === '/auth/callback' || parsed.pathname.endsWith('/auth/callback');

    if (!isNativeScheme && !isLegacyLocalhost && !isHttpsCallback && !hasAuthPayload(parsed)) {
      return null;
    }

    return `/auth/callback${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

let nativeOAuthListenerReady = false;

/**
 * Sur APK : le retour OAuth arrive via Custom Tab → deep link custom scheme
 * (com.arowtechnologie.isbd://login-callback). On ferme le navigateur système
 * et on route le WebView vers /auth/callback pour setSession depuis le hash.
 */
export function ensureNativeOAuthListener() {
  if (!isNativeApp() || nativeOAuthListenerReady) return;
  nativeOAuthListenerReady = true;

  void CapApp.addListener('appUrlOpen', ({ url }) => {
    const path = callbackPathFromUrl(url);
    if (!path) return;
    void Browser.close().catch(() => undefined);
    if (window.location.pathname === '/auth/callback') {
      const q = window.location.search + window.location.hash;
      if (q.includes('access_token=') || q.includes('token_hash=') || q.includes('token=')) {
        if (path !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
          window.location.replace(path);
        }
        return;
      }
    }
    window.location.replace(path);
  });
}

/** Lance le flux Google via le SDK (connexion ou création de compte). */
export async function startGoogleAuth(): Promise<{ error: Error | null }> {
  ensureNativeOAuthListener();

  const redirectTo = getOAuthRedirectUrl();
  const { data, error } = await kuunda.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      skipBrowserRedirect: true,
      queryParams: {
        access_type: 'offline',
        prompt: 'select_account',
      },
    },
  });
  if (error) return { error: new Error(error.message) };
  if (!data?.url) {
    return { error: new Error('Impossible de démarrer la connexion Google.') };
  }

  if (isNativeApp()) {
    try {
      await Browser.open({ url: data.url });
    } catch (err) {
      return {
        error: err instanceof Error ? err : new Error('Impossible d’ouvrir Google.'),
      };
    }
    return { error: null };
  }

  window.location.assign(data.url);
  return { error: null };
}
